import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, DataTable, EmptyState, ErrorState } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import { HealthIndicator } from '../../_shared/components/HealthIndicator';
import { formatGatewayDate, normalizeHealthState } from '../../_shared/utils/format';
import { useTargetsQuery } from '../hooks/useTargets';
import { TargetDetailDialog } from './TargetDetailDialog';
import type { TargetRow, TargetFilters } from '../types/target';
import type { HealthState } from '../../_shared/types/shared';

function TableSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} style={{ height: '48px', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-md)' }} />
      ))}
    </div>
  );
}

/**
 * Gateway 타겟 전체 현황 (#621) — GET /api/eraf/gateway/targets, 전 서비스 대상 read-only 목록.
 *
 * BE 에 등록/수정/삭제/가중치 벌크변경 엔드포인트가 없으므로 조회 + 클라이언트 사이드
 * 필터링만 제공한다. 서비스 하나만 살펴볼 때는 ServicesListPage 상세 다이얼로그의 타겟 표가
 * 더 간결하며, 이 페이지는 서비스 전체를 가로지르는 헬스 현황 파악용이다.
 */
export function TargetsListPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<TargetFilters>({});
  const [selected, setSelected] = useState<TargetRow | null>(null);

  const { data, isLoading, isError, refetch } = useTargetsQuery();

  const serviceOptions = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.map((row) => row.serviceId))).sort();
  }, [data]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const needle = filters.q?.trim().toLowerCase();
    return data.filter((row) => {
      if (filters.serviceId && row.serviceId !== filters.serviceId) return false;
      if (filters.health && normalizeHealthState(row.healthStatus) !== filters.health) return false;
      if (needle && !`${row.host}:${row.port}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [data, filters]);

  const columns: DataTableColumn<TargetRow>[] = [
    {
      key: 'service',
      header: t('gateway.targets.columns.service'),
      width: '140px',
      render: (row) => (
        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>{row.serviceId}</span>
      ),
    },
    {
      key: 'hostPort',
      header: t('gateway.targets.columns.host'),
      render: (row) => (
        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)' }}>
          {row.host}:{row.port}
        </span>
      ),
    },
    { key: 'weight', header: t('gateway.targets.columns.weight'), width: '90px' },
    { key: 'source', header: t('gateway.targets.columns.source'), width: '110px' },
    {
      key: 'health',
      header: t('gateway.targets.columns.health'),
      width: '150px',
      render: (row) => (
        <HealthIndicator state={normalizeHealthState(row.healthStatus)} size="sm" showLabel latencyMs={row.lastLatencyMs ?? undefined} />
      ),
    },
    {
      key: 'lastCheckedAt',
      header: t('gateway.targets.columns.lastCheckedAt'),
      width: '120px',
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {row.lastCheckedAt ? formatGatewayDate(row.lastCheckedAt) : '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('gateway.targets.columns.actions'),
      width: '100px',
      render: (row) => (
        <button
          type="button"
          aria-label={`${row.host}:${row.port} ${t('gateway.targets.actions.viewDetail')}`}
          onClick={(e) => { e.stopPropagation(); setSelected(row); }}
          style={actionBtnStyle}
        >
          {t('gateway.targets.actions.viewDetail')}
        </button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title={t('gateway.targets.title')} breadcrumb={t('gateway.targets.breadcrumb')} />

      {/* Filter Bar */}
      <div style={{ display: 'flex', gap: 'var(--spacing-3)', padding: 'var(--spacing-4) var(--spacing-6)', flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          aria-label={t('gateway.targets.filter.serviceFilter')}
          value={filters.serviceId ?? ''}
          onChange={(e) => setFilters({ ...filters, serviceId: e.target.value || undefined })}
          style={selectStyle}
        >
          <option value="">{t('gateway.targets.filter.allService')}</option>
          {serviceOptions.map((serviceId) => (
            <option key={serviceId} value={serviceId}>{serviceId}</option>
          ))}
        </select>

        <select
          aria-label={t('gateway.targets.filter.healthFilter')}
          value={filters.health ?? ''}
          onChange={(e) => setFilters({ ...filters, health: (e.target.value || undefined) as HealthState | undefined })}
          style={selectStyle}
        >
          <option value="">{t('gateway.targets.filter.allHealth')}</option>
          <option value="healthy">{t('gateway.shared.health.healthy')}</option>
          <option value="unhealthy">{t('gateway.shared.health.unhealthy')}</option>
          <option value="draining">{t('gateway.shared.health.draining')}</option>
          <option value="unknown">{t('gateway.shared.health.unknown')}</option>
        </select>

        <input
          type="search"
          aria-label={t('gateway.targets.filter.searchAriaLabel')}
          placeholder={t('gateway.targets.filter.searchPlaceholder')}
          value={filters.q ?? ''}
          onChange={(e) => setFilters({ ...filters, q: e.target.value || undefined })}
          style={{ ...selectStyle, flex: 1, minWidth: '200px' }}
        />
      </div>

      {/* Main Content */}
      <div style={{ padding: '0 var(--spacing-6) var(--spacing-6)' }}>
        {isLoading ? (
          <TableSkeleton />
        ) : isError ? (
          <ErrorState
            title={t('gateway.targets.list.error')}
            message={t('gateway.shared.error.networkErrorMessage')}
            onRetry={() => refetch()}
          />
        ) : filteredRows.length === 0 ? (
          <EmptyState title={t('gateway.targets.list.empty')} description={t('gateway.targets.list.emptyDesc')} />
        ) : (
          <DataTable columns={columns} rows={filteredRows} rowKey={(row) => row.id} onRowClick={(row) => setSelected(row)} />
        )}
      </div>

      {selected && <TargetDetailDialog target={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

const actionBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-secondary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};

const selectStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface-background)',
};
